import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import fr from './locales/fr.json';

const resources = {
  en: {
    translation: en
  },
  fr: {
    translation: fr
  }
};

// Function to detect browser language
const detectBrowserLanguage = (): string => {
  const browserLang = navigator.language || navigator.languages?.[0] || 'en';
  console.log('Browser language detected:', browserLang);
  
  if (browserLang.startsWith('fr')) {
    return 'fr';
  }
  return 'en'; // Default to English
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: process.env.NODE_ENV === 'development',
    
    interpolation: {
      escapeValue: false,
    },
    
    detection: {
      // Order of language detection methods - navigator first for browser language detection
      order: ['navigator', 'localStorage', 'cookie', 'htmlTag'],
      
      // Cache the detected language
      caches: ['localStorage'],
      
      // Convert language codes (e.g., 'fr-FR' -> 'fr', 'fr-CA' -> 'fr')
      convertDetectedLanguage: (lng: string) => {
        console.log('Converting detected language:', lng);
        
        // Handle French variants
        if (lng.startsWith('fr')) {
          console.log('French language detected, setting to fr');
          return 'fr';
        }
        // Handle English variants  
        if (lng.startsWith('en')) {
          console.log('English language detected, setting to en');
          return 'en';
        }
        // Return the base language code for other languages
        const baseLang = lng.split('-')[0];
        console.log('Other language detected, using base:', baseLang);
        return baseLang;
      }
    },
    
    // Supported languages
    supportedLngs: ['en', 'fr'],
    
    // Allow non-explicit language support
    nonExplicitSupportedLngs: true
  });

// Log the final detected language
i18n.on('initialized', () => {
  console.log('i18n initialized with language:', i18n.language);
});

i18n.on('languageChanged', (lng) => {
  console.log('Language changed to:', lng);
});

// Function to initialize language from user preferences
export const initializeUserLanguage = async () => {
  try {
    // Check if user is authenticated and get their preferred language
    const { supabase } = await import('@/integrations/supabase/client');
    const { data: { user } } = await supabase.auth.getUser();
    
    if (user) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('preferred_language')
        .eq('id', user.id)
        .single();
      
      if (profile?.preferred_language && ['en', 'fr'].includes(profile.preferred_language)) {
        console.log('Setting language from user preference:', profile.preferred_language);
        i18n.changeLanguage(profile.preferred_language);
        return;
      }
    }
    
    // Fallback to browser language detection
    const browserLang = detectBrowserLanguage();
    if (browserLang !== i18n.language) {
      console.log('Setting language from browser detection:', browserLang);
      i18n.changeLanguage(browserLang);
    }
  } catch (error) {
    console.error('Error initializing user language:', error);
    // Fallback to browser language detection
    const browserLang = detectBrowserLanguage();
    if (browserLang !== i18n.language) {
      i18n.changeLanguage(browserLang);
    }
  }
};

export default i18n; 